// Cache surface + pure logic TRANSFERRED from
// `src/hooks/query/useQueryUserBillingInfo.ts` (now a delegate) at D3,
// ARCFE-977.
//
// A mixed shim, not a wholesale move. What crossed is everything that is
// really commerce's: the frozen cache identity, the module-scope commands
// other domains call, the pure trial-derivation, and the request. What stayed
// is app composition and one STAYING surface:
//   - the merge of the dev-harness override. The override lives in this
//     package (`../billing-dev/overrides`), but the app's hook still merges it
//     ON READ in its `select`, so a refetch cannot wipe it.
//   - the trial countdown math crosses the SEAM rather than moving: it bottoms
//     out in `parseTimeStamp`, which reads the account timezone.
//   - the store token, the Kinde-region gate and the user read are app
//     composition (the `enabled` decision crosses as a boolean).
//   - `queryFn` toasted on an axios error. A user-visible effect is an app
//     reaction, so it becomes a port fired at the same position.

import { queryOptions, useQuery } from '@tanstack/react-query';
import { GET_USER_BILLING_INFO, type UserBillingInfo } from '../../contracts';
import { getUserBillingInfo } from '../api/billing';
import { getCommerceDataConfig } from '../config';

export type QueryUserBillingInfoResponse = UserBillingInfo & {
  trialDaysLeft?: number;
  trialHoursLeft?: number;
};

/**
 * Derives the display-only trial counters.
 *
 * Pure and moved verbatim, including the branch that only computes them for a
 * REVERSE free trial and the `isScribePlus ? undefined : (affiliate ? 30 : 14)`
 * ceiling. That ceiling decides what a user is told about time remaining, so
 * the characterization pins each arm rather than trusting the move.
 */
export function buildQueryUserBillingInfoResponse(
  billingInfo: UserBillingInfo,
  affiliateCode: string | null | undefined
): QueryUserBillingInfoResponse {
  const { calculateTrialDaysLeft, calculateTrialHoursLeft } =
    getCommerceDataConfig().trialCalculators;

  if (billingInfo.is_in_reverse_free_trial) {
    const isScribePlus = !billingInfo?.is_reverse_trial_extendable;
    const maxTrialDays = isScribePlus ? (affiliateCode ? 30 : 14) : undefined;

    const trialDaysLeft = calculateTrialDaysLeft(
      billingInfo?.is_in_free_trial,
      billingInfo?.trial_end_date,
      maxTrialDays
    );
    const trialHoursLeft = calculateTrialHoursLeft(
      billingInfo?.is_in_free_trial,
      billingInfo?.trial_end_date
    );
    return {
      ...billingInfo,
      trialDaysLeft,
      trialHoursLeft,
    };
  }
  return billingInfo;
}

/** Peek at the cached billing info without subscribing. */
export const getCurrentUserBillingInfo = () =>
  getCommerceDataConfig().queryClient.getQueryData<QueryUserBillingInfoResponse>([
    GET_USER_BILLING_INFO,
  ]);

/**
 * The invalidator half a dozen commerce mutations call.
 *
 * This is the function the whole D2 sequencing turned on: hooks that merely
 * INVALIDATE billing could move behind a port long before the cache itself
 * transferred, because an invalidation is a command, not a read.
 */
export const invalidateCurrentUserBillingInfo = () =>
  getCommerceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_USER_BILLING_INFO],
  });

/**
 * Immediately updates cached billing (e.g. after trial activation returns
 * fresh `billing_info`).
 *
 * `affiliateCode` is now a PARAMETER. The legacy version read it internally
 * via `getCurrentUser()` — a workspace `/data` call this package may not
 * make — so the app delegate supplies it and the behaviour is unchanged.
 */
export const setUserBillingInfoQueryCacheFromBillingInfo = (
  billingInfo: UserBillingInfo | null | undefined,
  affiliateCode: string | null | undefined
) => {
  if (billingInfo == null) {
    return;
  }
  getCommerceDataConfig().queryClient.setQueryData<QueryUserBillingInfoResponse>(
    [GET_USER_BILLING_INFO],
    buildQueryUserBillingInfoResponse(billingInfo, affiliateCode)
  );
};

export type UserBillingInfoQueryOptions = {
  /** `!!token && !!kindeProperties?.region && !!user`, resolved app-side. */
  enabled: boolean;
  affiliateCode: string | null | undefined;
  /**
   * Fires where `queryFn` toasted. The delegate still decides WHETHER to
   * toast: the legacy code toasted only for an axios error that carried a
   * status and a body, and re-threw everything. That asymmetry stays app-side
   * with the axios check it depends on.
   */
  onBillingFetchError?: (error: unknown) => void;
};

/**
 * The user billing query.
 *
 * `refetchOnWindowFocus: true` is kept explicitly even though it may match the
 * client default — billing drives entitlement gating, and an unfocused tab
 * showing a stale plan is the failure this opts into avoiding.
 */
export const userBillingInfoQueryOptions = ({
  enabled,
  affiliateCode,
  onBillingFetchError,
}: UserBillingInfoQueryOptions) =>
  queryOptions({
    queryKey: [GET_USER_BILLING_INFO],
    enabled,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<QueryUserBillingInfoResponse> => {
      try {
        const { data } = await getUserBillingInfo();

        if (!data) {
          throw new Error('Failed to fetch user billing info');
        }

        const billingInfo = data.billing_info;

        if (billingInfo == null) {
          return billingInfo;
        }
        return buildQueryUserBillingInfoResponse(billingInfo, affiliateCode);
      } catch (error) {
        onBillingFetchError?.(error);
        throw error;
      }
    },
  });

/**
 * Subscribe to billing info the app delegate already caches.
 *
 * Package `/ui` must not re-compose token, region, affiliate or dev-override
 * gates — those stay in `src/hooks/query/useQueryUserBillingInfo.ts`. Same
 * query key, so cache writes from the delegate re-render subscribers here.
 */
export function useQueryUserBillingInfo<T = QueryUserBillingInfoResponse>(
  select?: (data: QueryUserBillingInfoResponse) => T
) {
  return useQuery({
    ...userBillingInfoQueryOptions({
      enabled: false,
      affiliateCode: undefined,
    }),
    select,
  });
}
